import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faIndustry,
  faTruck,
  faCheese,
  faArrowUp,
  faMinus,
} from "@fortawesome/free-solid-svg-icons";
import { stockService } from "../services/stock";
import HistoricoRecente from "../components/HistoricoRecente"; // Importar o novo componente
import ProductionSuggestions from "../components/ProductionSuggestions";
import StockAlerts from "../components/StockAlerts";
import ReadyProductsOverview from "../components/ReadyProductsOverview";
import { dashboardService } from "../services/dashboard";
import toast from "react-hot-toast";

const currentMonthValue = () => new Date().toISOString().slice(0, 7);

const Dashboard = () => {
  const [totalStock, setTotalStock] = useState(null);
  const [monthlyProduction, setMonthlyProduction] = useState(null);
  const [monthlyExpedition, setMonthlyExpedition] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(currentMonthValue());
  const [expeditionStatusFilter, setExpeditionStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  
  // Novos estados para os indicadores
  const [productionSuggestions, setProductionSuggestions] = useState([]);
  const [stockAlerts, setStockAlerts] = useState([]);
  const [readyProducts, setReadyProducts] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(true);
  const [loadingAlerts, setLoadingAlerts] = useState(true);
  const [loadingReady, setLoadingReady] = useState(true);
  const [suggestionPriorityFilter, setSuggestionPriorityFilter] = useState("");
  const [alertTypeFilter, setAlertTypeFilter] = useState("");
  const [readyStatusFilter, setReadyStatusFilter] = useState("");

  // Estado para baixas manuais
  const [manualWithdrawals, setManualWithdrawals] = useState([]);
  const [loadingWithdrawals, setLoadingWithdrawals] = useState(true);


  // Função para buscar dados dos novos indicadores
  const fetchIndicatorsData = async () => {
    try {
      // Buscar dados em paralelo
      const [suggestionsData, alertsData, productsData] = await Promise.all([
        dashboardService.getProductionSuggestions().catch(err => {
          console.warn("Erro ao buscar sugestões de produção:", err);
          toast.error("Erro ao buscar sugestões de produção: " + (err.message || "Erro desconhecido"));
          return { suggestions: [], cache_info: null };
        }),
        dashboardService.getStockAlerts().catch(err => {
          console.warn("Erro ao buscar alertas de stock:", err);
          toast.error("Erro ao buscar alertas de stock: " + (err.message || "Erro desconhecido"));
          return [];
        }),
        dashboardService.getReadyProducts().catch(err => {
          console.warn("Erro ao buscar produtos prontos:", err);
          toast.error("Erro ao buscar produtos prontos: " + (err.message || "Erro desconhecido"));
          return [];
        })
      ]);

      // Extrair dados e info de cache
      const suggestions = Array.isArray(suggestionsData.suggestions) ? suggestionsData.suggestions : (Array.isArray(suggestionsData) ? suggestionsData : []);
      setProductionSuggestions(suggestions.filter((suggestion) =>
        Number(suggestion.suggested_quantity ?? suggestion.suggestedQuantity ?? 0) > 0
      ));
      setStockAlerts(Array.isArray(alertsData) ? alertsData.filter((alert) =>
        alert.alert_type &&
        (alert.alert_type === 'excesso' || alert.days_to_expiry === null || Number(alert.days_to_expiry) >= 0)
      ) : []);
      setReadyProducts(Array.isArray(productsData) ? productsData.filter((product) =>
        Number(product.current_stock_kg || 0) > 0 || Number(product.quantity_curing_kg || 0) > 0
      ) : []);
    } catch (error) {
      console.error("Erro ao carregar indicadores:", error);
      toast.error("Erro ao carregar indicadores: " + (error.message || "Erro desconhecido"));
    } finally {
      setLoadingSuggestions(false);
      setLoadingAlerts(false);
      setLoadingReady(false);
    }
  };

  // Função para buscar baixas manuais
  const fetchManualWithdrawals = async () => {
    try {
      const data = await stockService.getManualWithdrawals({ limit: 10 });
      // Garantir que é um array - o backend pode retornar { data: [...] } ou diretamente [...]
      const withdrawalsArray = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
      setManualWithdrawals(withdrawalsArray);
    } catch (error) {
      console.error("Erro ao buscar baixas manuais:", error);
      // Não mostrar toast aqui para não poluir a interface
      setManualWithdrawals([]); // Fallback para array vazio em caso de erro
    } finally {
      setLoadingWithdrawals(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [year, month] = selectedMonth.split("-");
        const dashboardFilters = {
          year,
          month,
          ...(expeditionStatusFilter ? { status: expeditionStatusFilter } : {}),
        };
        
        // Buscar dados de stock
        try {
          const stockData = await stockService.getTotalStock();
          setTotalStock(stockData.total_stock_kg);
        } catch (err) {
          console.error("Erro ao buscar dados de stock:", err);
          toast.error("Erro ao buscar dados de stock: " + (err.message || "Erro desconhecido"));
          setTotalStock(null);
        }

        try {
          const productionData = await dashboardService.getMonthlyProductionTotal(dashboardFilters);
          setMonthlyProduction(Number(productionData.total_production_kg || 0));
        } catch (err) {
          console.error("Erro ao buscar produção mensal:", err);
          toast.error("Erro ao buscar produção mensal: " + (err.message || "Erro desconhecido"));
          setMonthlyProduction(null);
        }

        try {
          const expeditionData = await dashboardService.getMonthlyExpeditionTotal(dashboardFilters);
          setMonthlyExpedition(Number(expeditionData.total_expedition_kg || 0));
        } catch (err) {
          console.error("Erro ao buscar expedição mensal:", err);
          toast.error("Erro ao buscar expedição mensal: " + (err.message || "Erro desconhecido"));
          setMonthlyExpedition(null);
        }

        // Buscar dados dos novos indicadores
        await fetchIndicatorsData();

        // Buscar baixas manuais
        await fetchManualWithdrawals();

        setError("");
      } catch (err) {
        setError(err.message || "Erro ao carregar dados do dashboard.");
        toast.error("Erro ao carregar dados do dashboard: " + (err.message || "Erro desconhecido"));
        console.error("Erro ao carregar dados do dashboard:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedMonth, expeditionStatusFilter]);

  if (loading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 text-center">
        Carregando dados do dashboard...
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 text-center text-red-600">
        {error}
      </div>
    );
  }

  const noData =
    totalStock === null &&
    monthlyProduction === null &&
    monthlyExpedition === null;

  if (noData) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 text-center text-neutral-500">
        Não possui dados suficientes para exibir.
      </div>
    );
  }

  const filteredProductionSuggestions = productionSuggestions
    .filter((suggestion) => !suggestionPriorityFilter || suggestion.production_priority === suggestionPriorityFilter)
    .slice(0, 10);
  const filteredStockAlerts = stockAlerts
    .filter((alert) => !alertTypeFilter || alert.alert_type === alertTypeFilter)
    .slice(0, 10);
  const filteredReadyProducts = readyProducts
    .filter((product) => !readyStatusFilter || product.stock_status === readyStatusFilter)
    .slice(0, 10);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900">Dashboard</h1>
          <p className="text-sm text-neutral-500">Indicadores calculados com dados reais do período selecionado.</p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <label className="text-sm text-neutral-600">
            Mês
            <input
              type="month"
              value={selectedMonth}
              onChange={(event) => setSelectedMonth(event.target.value || currentMonthValue())}
              className="mt-1 block rounded-md border border-neutral-300 px-3 py-2 text-sm"
            />
          </label>
          <label className="text-sm text-neutral-600">
            Status expedição
            <select
              value={expeditionStatusFilter}
              onChange={(event) => setExpeditionStatusFilter(event.target.value)}
              className="mt-1 block rounded-md border border-neutral-300 px-3 py-2 text-sm"
            >
              <option value="">Todos</option>
              <option value="pending">Pendente</option>
              <option value="packed">Embalado</option>
              <option value="delivered">Entregue</option>
              <option value="cancelled">Cancelado</option>
              <option value="divided">Dividida</option>
            </select>
          </label>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {/* KPI Cards */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <h3 className="text-neutral-500 text-sm">Stock Total</h3>
            <FontAwesomeIcon icon={faCheese} className="text-neutral-400" />
          </div>
          <p className="mt-2 text-3xl">
            {totalStock !== null && totalStock !== undefined ? `${totalStock.toFixed(2)} kg` : "N/A"}
          </p>
          <p className="mt-2 text-sm text-neutral-500">
            <span className="text-neutral-500">
              <FontAwesomeIcon icon={faArrowUp} /> {/* Placeholder para % */}
            </span>
            stock disponível atual
          </p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <h3 className="text-neutral-500 text-sm">Produção Mensal</h3>
            <FontAwesomeIcon icon={faIndustry} className="text-neutral-400" />
          </div>
          <p className="mt-2 text-3xl">
            {monthlyProduction !== null && monthlyProduction !== undefined
              ? `${monthlyProduction.toFixed(2)} kg`
              : "N/A"}
          </p>
          <p className="mt-2 text-sm text-neutral-500">
            Lotes produzidos no período
          </p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <h3 className="text-neutral-500 text-sm">Expedição Mensal</h3>
            <FontAwesomeIcon icon={faTruck} className="text-neutral-400" />
          </div>
          <p className="mt-2 text-3xl">
            {monthlyExpedition !== null && monthlyExpedition !== undefined
              ? `${monthlyExpedition.toFixed(2)} kg`
              : "N/A"}
          </p>
          <p className="mt-2 text-sm text-neutral-500">
            <span className="text-neutral-500">
              <FontAwesomeIcon icon={faArrowUp} /> {/* Placeholder para % */}
            </span>
            peso expedido no período
          </p>
        </div>
      </div>

      {/* Novos Indicadores Solicitados - 3 Paineis Lado a Lado */}
      <div className="mt-8 bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <label className="text-sm text-neutral-600">
            Sugestões
            <select
              value={suggestionPriorityFilter}
              onChange={(event) => setSuggestionPriorityFilter(event.target.value)}
              className="mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
            >
              <option value="">Todas as prioridades</option>
              <option value="URGENT">Urgentes</option>
              <option value="HIGH">Altas</option>
              <option value="MEDIUM">Médias</option>
              <option value="LOW">Baixas</option>
            </select>
          </label>
          <label className="text-sm text-neutral-600">
            Alertas
            <select
              value={alertTypeFilter}
              onChange={(event) => setAlertTypeFilter(event.target.value)}
              className="mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
            >
              <option value="">Todos os alertas</option>
              <option value="excesso">Excesso</option>
              <option value="mais curado">Mais curado</option>
              <option value="queijo perto do tempo de estragar">Perto do vencimento</option>
            </select>
          </label>
          <label className="text-sm text-neutral-600">
            Stock pronto
            <select
              value={readyStatusFilter}
              onChange={(event) => setReadyStatusFilter(event.target.value)}
              className="mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
            >
              <option value="">Todos os estados</option>
              <option value="LOW_STOCK">Baixo</option>
              <option value="EXCESS_STOCK">Excesso</option>
              <option value="NORMAL">Normal</option>
            </select>
          </label>
        </div>
      </div>
      <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
        <ProductionSuggestions
          suggestions={filteredProductionSuggestions}
          loading={loadingSuggestions}
        />
        <StockAlerts
          alerts={filteredStockAlerts}
          loading={loadingAlerts}
        />
        <ReadyProductsOverview
          products={filteredReadyProducts}
          loading={loadingReady}
        />
      </div>


      {/* Histórico de Alterações Recentes */}
      <div className="mt-8">
        <HistoricoRecente />
      </div>

      {/* Baixas Manuais Recentes */}
      <div className="mt-8 bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center">
            <FontAwesomeIcon icon={faMinus} className="mr-2 text-red-600" />
            Baixas Manuais Recentes
          </h2>
        </div>

        {loadingWithdrawals ? (
          <div className="text-center text-neutral-500 py-4">
            Carregando baixas manuais...
          </div>
        ) : manualWithdrawals.length === 0 ? (
          <div className="text-center text-neutral-500 py-4">
            Nenhuma baixa manual registrada.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Data
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Produto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Código
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Quantidade (kg)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Motivo
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-neutral-200">
                {manualWithdrawals.map((withdrawal) => (
                  <tr key={withdrawal.id} className="hover:bg-neutral-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-900">
                      {new Date(withdrawal.withdrawal_date).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-6 py-4 text-sm text-neutral-900">
                      {withdrawal.products?.name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
                      {withdrawal.products?.product_code || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                      {(withdrawal.quantity_kg || 0).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-sm text-neutral-600">
                      {withdrawal.reason}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
